"use client";

import React, { useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import { InsightsBlock } from "../index";

const Button = dynamic(() => import("@/Components/Button"));
const InsightsBlue = dynamic(() => import("./InsightsBlue"));
const InsightsCard = dynamic(() => import("./InsightsCard"));
const Section = dynamic(() => import("@/Components/LayoutComponents/Section"), {
  ssr: true,
});

type Insight = {
  id: number;
  varient: string;
  title: string;
  post_date: string;
  link: {
    href: string;
    label: string;
  };
};

type InsightsBlockWithInsights = InsightsBlock & {
  Insights?: Insight[];
};

interface InsightsProps {
  data: InsightsBlock
}

const Insights: React.FC<InsightsProps> = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const insightsBlock = data as InsightsBlockWithInsights;
  const insights = insightsBlock?.Insights || [];

  return (
    <Section>
      <div className="container">
        <div className="max-w-[1230px] mx-auto text-center">
          <h2 className="text-heading-1 text-primary-text font-bold capitalize">
            {data?.title}
          </h2>
          <p className="text-subtitle-2 font-regular mt-3 max-w-[894px] mx-auto">
            {data?.sub_title}
          </p>
        </div>
      </div>

      {/* Desktop grid view */}
      <div className="container">
        <div className="mt-10 lg:grid hidden grid-cols-2 xl:gap-[30px] lg:gap-[38px_30px]">
          {insights.slice(0, 3).map((insight, index) => {
            const commonProps = {
              title: insight.title || "",
              date: insight.post_date || "",
              link: insight.link.href || "",
              linkLabel: insight.link.label || "Read more",
            };
            return insight.varient === "blue" ? (
              <div
                className="lg:row-span-2 lg:col-span-1 col-span-2 h-full"
                key={`${insight.id}-${index}`}
              >
                <InsightsBlue {...commonProps} />
              </div>
            ) : (
              <div className="col-span-1" key={`${insight.id}-${index}`}>
                <InsightsCard {...commonProps} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile Swiper slider view */}
      <div className="lg:hidden block md:mt-10 mt-8 relative">
        <Swiper
          modules={[Autoplay]}
          loop={true}
          slidesPerView={1}
          centeredSlides={true}
          spaceBetween={15}
          speed={1000} // transition speed (ms)
          autoplay={{
            delay: 3000, // delay before next slide
            disableOnInteraction: false,
          }}
          onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
          className="w-[90%] mx-auto"
        >
          {insights.slice(0, 3).map((insight, index) => {
            const commonProps = {
              title: insight.title || "",
              date: insight.post_date || "",
              link: insight.link.href || "",
              linkLabel: insight.link.label || "Read more",
            };
            const SlideComponent =
              insight.varient === "blue" ? InsightsBlue : InsightsCard;

            return (
              <SwiperSlide key={`slide-${insight.id}-${index}`}>
                <SlideComponent {...commonProps} />
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Mobile Progress Bar */}
        <div className="mt-[30px] lg:mt-[15px] md:hidden flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative ">
          {insights.slice(0, 3).map((_, index) => (
            <div
              key={index}
              className={`p-0 relative rounded overflow-hidden ${currentIndex === index
                ? "flex w-[128px] h-1 bg-[#E6F3FD]"
                : "flex-none w-[16px] h-1 bg-[#E6F3FD]"
                }`}
            >
              <span
                className={`absolute top-0 left-0 rounded h-1 ${currentIndex === index
                  ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[3000ms] w-[128px]"
                  : "w-[16px]"
                  }`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* CTA Button */}
      {data?.link?.href && data?.link?.label && (
        <Link href={data?.link?.href || "/newsroom"}>
          <Button
            className="xl:mt-10 mt-8 mx-auto"
            label={data?.link?.label || ""}
          />
        </Link>
      )}
    </Section>
  );
};
export default React.memo(Insights);